import React, { useEffect, useState, useMemo } from "react";
import { auth, db } from "../../firebase";
import { signOut } from "firebase/auth";
import {
  collection,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";

const PAGE_SIZE = 10;

const Admin = () => {
  const [waitlist, setWaitlist] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchWaitlist = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, "waitlist"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const emails = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || null,
        }));
        setWaitlist(emails);
      } catch (error) {
        console.error("Error fetching waitlist:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWaitlist();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const sortedWaitlist = useMemo(() => {
    let sortableItems = [...waitlist];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (aVal === null) return 1;
        if (bVal === null) return -1;

        if (aVal instanceof Date && bVal instanceof Date) {
          aVal = aVal.getTime();
          bVal = bVal.getTime();
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [waitlist, sortConfig]);

  const filteredWaitlist = sortedWaitlist.filter((item) =>
    item.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredWaitlist.length / PAGE_SIZE);
  const paginatedWaitlist = filteredWaitlist.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const requestSort = (key) => {
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-bold">Admin Dashboard</h1>
        <button
          onClick={handleLogout}
          className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
        >
          Logout
        </button>
      </div>

      <div className="mb-4 flex justify-between items-center">
        <input
          type="search"
          placeholder="Search emails..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          className="border rounded px-4 py-2 w-full max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <p>Loading waitlist...</p>
      ) : (
        <div className="overflow-x-auto border rounded shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th
                  onClick={() => requestSort("email")}
                  className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider select-none"
                >
                  Email
                  {sortConfig.key === "email" ? (
                    sortConfig.direction === "asc" ? " ▲" : " ▼"
                  ) : null}
                </th>
                <th
                  onClick={() => requestSort("createdAt")}
                  className="cursor-pointer px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider select-none"
                >
                  Joined At
                  {sortConfig.key === "createdAt" ? (
                    sortConfig.direction === "asc" ? " ▲" : " ▼"
                  ) : null}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedWaitlist.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-6 py-4 text-center text-gray-500">
                    No emails found.
                  </td>
                </tr>
              ) : (
                paginatedWaitlist.map(({ id, email, createdAt }) => (
                  <tr key={id}>
                    <td className="px-6 py-4 whitespace-nowrap">{email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {createdAt
                        ? createdAt.toLocaleString()
                        : "No date available"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-center space-x-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded border ${
            currentPage === 1
              ? "cursor-not-allowed opacity-50"
              : "hover:bg-gray-200"
          }`}
        >
          Previous
        </button>

        <span className="px-3 py-1 border rounded">
          Page {currentPage} of {totalPages || 1}
        </span>

        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages || totalPages === 0}
          className={`px-3 py-1 rounded border ${
            currentPage === totalPages || totalPages === 0
              ? "cursor-not-allowed opacity-50"
              : "hover:bg-gray-200"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Admin;
